import * as R from 'ramda';
import * as P from 'plow-js';
import { connect } from 'react-redux';
import React, { useState, useCallback } from 'react';
import { pure, compose, withState, withHandlers, lifecycle } from 'react-recompose';
// components
import { openModal, closeModal } from '../../../components/modal/actions';
import { openLoader, closeLoader } from '../../../components/loader/actions';
// features
import { withDispatchCarrierRate } from '../../mail-sending/hocs';
import { createDriverOnDriversCardRequest } from '../../drivers-card/actions';
// helpers/constants
import * as G from '../../../helpers';
import * as GC from '../../../constants';
// forms
import { LocationTemplateSearch } from '../../../forms/forms/template-search/components/location-template-search';
// hocs
import { withAsyncDistanceCalc, withAsyncSearchTemplateAutocomplete } from '../../../hocs';
// utilities
import { sendRequest } from '../../../utilities/http';
import endpointsMap from '../../../utilities/endpoints';
// feature rate
import { mapCarrierRatesWithProperQuantity } from '../helpers';
import IntegrationErrors from '../components/integration-errors';
import { carrierOptionFields, dispatcherOptionFields } from '../carrier/settings';
import {
  createTelRateActionRequest,
  updateTelRateActionRequest,
  createTelCarrierRateActionRequest,
  updateTelCarrierRateActionRequest,
} from '../actions';
//////////////////////////////////////////////////

const useCarrierRates = () => {
  const [state, setState] = useState({ loading: true, rateInfo: null, carrierRates: [] });

  const getAutoRatesRequest = useCallback(async (props) => {
    const {
      telGuid,
      loadData,
      equipments,
      branchGuid,
      openLoader,
      serviceType,
      closeLoader,
      transportationMode,
      customerEnterpriseGuid,
    } = props;

    const currentUserBranchGuid = G.getAmousCurrentUserBranchGuidFromWindow();

    const options = {
      data: {
        loadData,
        equipments,
        serviceType,
        loadGuid: telGuid,
        transportationMode,
        customerEnterpriseGuid,
        [GC.BRANCH_GUID]: R.or(branchGuid, currentUserBranchGuid),
      },
    };
    G.callFunction(openLoader);

    const res = await sendRequest('post', endpointsMap.telAvailableCarriersRate, options);

    const { data, status } = res;

    if (R.and(G.isResponseSuccess(status), G.isNotEmpty(data))) {
      const rates = data.rateData.map(
        (item) => R.mergeRight(item, R.prop(GC.SYSTEM_OBJECT_CARRIER_SNAPSHOT, item)),
      );

      const mapped = mapCarrierRatesWithProperQuantity(rates);

      setState({ loading: false, rateInfo: data, carrierRates: mapped });
    } else {
      setState((prev) => ({ ...prev, loading: false }));

      G.handleFailResponseSimple(
        res,
        true,
        'withAsyncCarrierRates -> getAutoRatesRequest',
      );
    }

    G.callFunction(closeLoader);
  }, []);

  const getTelRateCarrierRatingInfoRequest = useCallback(async (props) => {
    const {
      telGuid,
      loadData,
      equipments,
      branchGuid,
      openLoader,
      serviceType,
      closeLoader,
      transportationMode,
      customerEnterpriseGuid,
    } = props;

    const currentUserBranchGuid = G.getAmousCurrentUserBranchGuidFromWindow();

    const options = {
      data: {
        loadData,
        equipments,
        serviceType,
        loadGuid: telGuid,
        transportationMode,
        customerEnterpriseGuid,
        [GC.BRANCH_GUID]: R.or(branchGuid, currentUserBranchGuid),
      },
    };

    G.callFunction(openLoader);

    const res = await sendRequest('post', endpointsMap.telRateCarrierRatingInfo, options);

    const { data, status } = res;

    if (R.and(G.isResponseSuccess(status), G.isNotEmpty(data))) {
      setState((prev) => ({ ...prev, loading: false, rateInfo: data }));
    } else {
      setState((prev) => ({ ...prev, loading: false }));

      G.handleFailResponseSimple(
        res,
        false,
        'withAsyncCarrierRates -> getTelRateCarrierRatingInfoRequest',
      );
    }

    G.callFunction(closeLoader);
  }, []);

  return { ...state, getAutoRatesRequest, getTelRateCarrierRatingInfoRequest };
};

export const withAsyncCarrierRates = (Component) => (props) => {
  const carrierRates = useCarrierRates();

  return <Component {...props} {...carrierRates} />;
};

const carrierDefaultFilter = [{
  operation: 'equal',
  booleanValue: true,
  dataType: 'boolean',
  propertyName: GC.FIELD_CARRIER_ACTIVE,
}];

const dispatchedByDefaultFilter = [{
  operation: 'in',
  dataType: 'string',
  stringValue: 'GENERAL',
  propertyName: GC.FIELD_USER_TYPE,
}];

const getDispatchedBySearchFilter = (searchText) => ([
  {
    dataType: 'string',
    operation: 'contain',
    stringValue: searchText,
    propertyName: GC.FIELD_FIRST_NAME,
    groupId: 'dispatchedBySearchFilter',
  },
  {
    dataType: 'string',
    operation: 'contain',
    stringValue: searchText,
    propertyName: GC.FIELD_LAST_NAME,
    groupId: 'dispatchedBySearchFilter',
  },
  {
    dataType: 'string',
    operation: 'contain',
    stringValue: searchText,
    groupId: 'dispatchedBySearchFilter',
    propertyName: GC.FIELD_USER_LOGIN_ID,
  },
]);

const carrierAssignmentRequestParams = {
  fieldName: GC.FIELD_CARRIER_NAME,
  optionFields: carrierOptionFields,
  defaultFilter: carrierDefaultFilter,
};

const dispatchedByRequestParams = {
  fieldName: GC.FIELD_FIRST_NAME,
  optionFields: dispatcherOptionFields,
  defaultFilter: dispatchedByDefaultFilter,
};

const setSearchCriteria = ({ props, defaultFilter, searchText = null }) => {
  if (G.isNilOrEmpty(searchText)) return defaultFilter;

  const defaultSearchFilter = {
    dataType: 'string',
    operation: 'contain',
    stringValue: searchText,
    propertyName: props.item.fieldName,
  };

  const searchFilter = G.ifElse(
    R.equals(props.item.fieldName, GC.FIELD_DISPATCHED_BY_LOGIN),
    getDispatchedBySearchFilter(searchText),
    R.of([], defaultSearchFilter),
  );

  return R.concat(defaultFilter, searchFilter);
};

const setRequestParams = ({
  props,
  fieldName,
  searchText,
  optionFields,
  defaultFilter,
  isPagination = false,
}) => {
  const offset = G.ifElse(
    R.and(isPagination, G.isNotEmpty(props.options)),
    R.length(props.options),
    0,
  );

  const orderFields = [{ sequence: 1, name: fieldName, reference: false, order: GC.SORT_TYPE_ASC }];

  const fields = optionFields.map(({ name, collection }, sequence) => ({
    name,
    sequence,
    freezed: false,
    reference: false,
    collection: false,
  }));

  return {
    offset,
    fields,
    limit: 10,
    orderFields,
    [GC.FIELD_CURRENT_BRANCH]: props.branchGuid,
    searchCriteria: setSearchCriteria({ props, searchText, defaultFilter }),
  };
};

async function loadCarrierOptions(props) {
  const reqData = setRequestParams({ ...carrierAssignmentRequestParams, props });

  const { data } = await sendRequest('post', endpointsMap.carrierListParentAndCurrent, { data: reqData });

  props.setCarrierOptions(data.results);
  props.setTotalCount(data.totalCount);
  props.setLoadingStatus(false);
}

async function loadDispatcherOptions(props) {
  const { setLoadingStatus, setDispatcherOptions, setDispatcherTotalCount } = props;

  try {
    const reqData = setRequestParams({ ...dispatchedByRequestParams, props });

    const { data } = await sendRequest('post', endpointsMap.listUsers, { data: reqData });

    setDispatcherOptions(data.results);
    setDispatcherTotalCount(data.totalCount);
  } catch (error) {
    G.handleException('error', 'loadDispatcherOptions exception');
  } finally {
    setLoadingStatus(false);
  }
}

export async function loadTerminalOptions(
  setTerminalOptions,
  carrierGuid,
  setLoadingStatus,
  setFieldValue,
) {
  const params = { carrierGuid };
  const res = await sendRequest('get', endpointsMap.carrierRateTerminalList, { params });
  const { data } = res;
  R.forEach(
    (terminal) => {
      if (terminal.primary) {
        setFieldValue(`${GC.FIELD_PREFIX_CARRIER_ASSIGNMENT}.${GC.FIELD_CARRIER_TERMINAL_GUID}`, terminal.guid);
      }
    },
    data,
  );
  setTerminalOptions(data);
  setLoadingStatus(false);
}

async function paginationLoadOptions(props) {
  const {
    field,
    entityList,
    dispatcherList,
    setLoadingStatus,
    setCarrierOptions,
    setDispatcherOptions,
  } = props;

  try {
    const isDispatchedByField = R.equals(field.name, GC.FIELD_DISPATCHED_BY_LOGIN);

    const requestParams = G.ifElse(
      isDispatchedByField,
      { ...dispatchedByRequestParams, props, isPagination: true },
      { ...carrierAssignmentRequestParams, props, isPagination: true },
    );

    const endpoint = G.ifElse(
      isDispatchedByField,
      endpointsMap.listUsers,
      endpointsMap.carrierListParentAndCurrent,
    );

    const reqData = setRequestParams(requestParams);

    const { data } = await sendRequest('post', endpoint, { data: reqData });

    const newOptions = G.ifElse(
      isDispatchedByField,
      R.concat(dispatcherList, R.or(data.results, [])),
      R.concat(entityList, R.or(data.results, [])),
    );

    if (isDispatchedByField) {
      setDispatcherOptions(newOptions);
    } else {
      setCarrierOptions(newOptions);
    }
  } catch (error) {
    G.handleException('error', 'paginationLoadOptions exception');
  } finally {
    setLoadingStatus(false);
  }
}

async function asyncSearch(props, searchText) {
  const {
    field,
    setTotalCount,
    setLoadingStatus,
    setCarrierOptions,
    setDispatcherOptions,
    setDispatcherTotalCount,
  } = props;

  try {
    const isDispatchedByField = R.equals(field.name, GC.FIELD_DISPATCHED_BY_LOGIN);

    const requestParams = {
      ...(isDispatchedByField ? dispatchedByRequestParams : carrierAssignmentRequestParams),
      props,
      searchText,
      isPagination: false,
    };

    const endpoint = G.ifElse(
      isDispatchedByField,
      endpointsMap.listUsers,
      endpointsMap.carrierListParentAndCurrent,
    );

    const reqData = setRequestParams(requestParams);

    const { data } = await sendRequest('post', endpoint, { data: reqData });

    const { results, totalCount } = data;

    if (isDispatchedByField) {
      setDispatcherOptions(results);
      setDispatcherTotalCount(totalCount);
    } else {
      setCarrierOptions(results);
      setTotalCount(totalCount);
    }
  } catch (error) {
    G.handleException('error', 'asyncSearch exception');
  } finally {
    setLoadingStatus(false);
  }
}

export const withAsyncCarrierSelect = compose(
  withState('isLoading', 'setLoadingStatus', false),
  withState('searchText', 'setText', null),
  withHandlers({
    handleScroll: (props) => () => {
      const {
        field,
        totalCount,
        entityList,
        dispatcherList,
        setLoadingStatus,
        dispatcherTotalCount,
      } = props;

      const condition = G.ifElse(
        R.equals(field.name, GC.FIELD_DISPATCHED_BY_LOGIN),
        R.gt(dispatcherTotalCount, dispatcherList.length),
        R.gt(totalCount, entityList.length),
      );

      if (condition) {
        setLoadingStatus(true);
        paginationLoadOptions(props);
      }
    },
    handleGetCarrierOptions: (props) => () => {
      if (R.isNil(props.totalCount)) {
        props.setLoadingStatus(true);
        loadCarrierOptions(props);
      }
    },
    handleSearch: (props) => (search) => {
      if (R.equals(props.field.name, `${GC.FIELD_PREFIX_CARRIER_ASSIGNMENT}.${GC.FIELD_CARRIER_TERMINAL_GUID}`)) return;
      const cond = R.not(R.and(G.isNilOrEmpty(props.searchText), R.isEmpty(search)));
      if (cond) {
        props.setLoadingStatus(true);
        asyncSearch(props, search, props.field);
        props.setText(search);
      }
    },
    handlerSelect: (props) => (option) => {
      props.handler(R.or(option, {}), props.field.name);

      if (G.isAllTrue(
        G.isNotNil(option),
        G.notEquals(props.field.name, GC.FIELD_DISPATCHED_BY_LOGIN),
        G.notEquals(props.field.name, `${GC.FIELD_PREFIX_CARRIER_ASSIGNMENT}.${GC.FIELD_CARRIER_TERMINAL_GUID}`),
      )) {
        loadTerminalOptions(
          props.setTerminalOptions,
          option.value,
          props.setLoadingStatus,
          props.setFieldValue,
        );
      }

      props.setText(null);
    },
    handleGetDispatcherOptions: (props) => () => {
      if (R.isNil(props.dispatcherTotalCount)) {
        props.setLoadingStatus(true);
        loadDispatcherOptions(props);
      }
    },
  }),
  pure,
);

export const enhanceRateCharges = (fromPage='default') => compose(
  connect(
    null,
    {
      createTelRateActionRequest,
      updateTelRateActionRequest,
      createDriverOnDriversCardRequest,
      createTelCarrierRateActionRequest,
      updateTelCarrierRateActionRequest,
    },
  ),
  withHandlers({
    handleCreateTelRate: (props) => (values) => {
      const {
        createTelRateActionRequest,
        createLoadDriverRateRequest,
        createDriverOnDriversCardRequest,
      } = props;

      const actionMap = {
        default: createTelRateActionRequest,
        driversCard: createDriverOnDriversCardRequest,
        rateLoadDetailsNew: createLoadDriverRateRequest,
      };

      actionMap[fromPage](values);
    },
    handleCreateCarrierTelRate: (props) => (values) => {
      const {
        createLoadCarrierRateRequest,
        createTelCarrierRateActionRequest,
      } = props;

      const actionMap = {
        default: createTelCarrierRateActionRequest,
        rateLoadDetailsNew: createLoadCarrierRateRequest,
      };

      actionMap[fromPage](values);
    },
    handleUpdateTelRate: (props) => (values) => {
      const {
        updateTelRateActionRequest,
        updateLoadDriverRateRequest,
      } = props;

      const actionMap = {
        default: updateTelRateActionRequest,
        rateLoadDetailsNew: updateLoadDriverRateRequest,
      };

      actionMap[fromPage](values);
    },
    handleUpdateCarrierTelRate: (props) => (values) => {
      const {
        updateLoadCarrierRateRequest,
        updateTelCarrierRateActionRequest,
      } = props;

      const actionMap = {
        default: updateTelCarrierRateActionRequest,
        rateLoadDetailsNew: updateLoadCarrierRateRequest,
      };

      actionMap[fromPage](values);
    },
  }),
);

export const withDispatchActions = compose(
  withHandlers({
    handleDispatchCarrierTelRate: (props) => (values) => {
      const { dispatchCarrierTelRateRequest } = props;

      const data = {
        ...values,
        mailTo: values[GC.FIELD_MAIL_SENDING_TO],
        telGuid: R.path([GC.FIELD_TEL, GC.FIELD_GUID], props),
      };

      dispatchCarrierTelRateRequest(R.omit([GC.FIELD_MAIL_SENDING_TO], data));
    },
  }),
  withDispatchCarrierRate,
  withHandlers({
    handleDispatchTelRate: (props) => () => {
      const {
        tel,
        dispatchTelRateRequest,
        handleDispatchCarrierRate,
        handleDispatchCarrierTelRate,
      } = props;

      const telGuid = R.prop(GC.FIELD_GUID, tel);
      const rate = R.path([GC.FIELD_TEL, GC.FIELD_RATE], props);

      const handleDispatchTelRate = () => {
        const dispatchData = {
          [GC.FIELD_GUID]: telGuid,
          dispatchAction: GC.DISPATCH_ACTION,
          [GC.FIELD_ORDER]: G.getPropFromObject(GC.FIELD_ORDER, tel),
        };

        dispatchTelRateRequest(dispatchData);
      };

      if (R.equals(rate[GC.FIELD_TYPE], GC.RATE_TYPE_CARRIER_RATE)) {
        const emailsSuspended = R.prop(GC.FIELD_EMAILS_SUSPENDED, rate);

        if (emailsSuspended) return G.callFunction(handleDispatchTelRate);

        const templateOptions = R.compose(
          R.map(({ name, guid }) => ({ label: name, value: guid })),
          R.pathOr([], ['documentTemplates', GC.DOCUMENT_PRINTABLE_SECTION_CARRIER_DISPATCH_DOCUMENTS]),
        )(props);

        const documents = R.pathOr(
          R.pathOr([], ['asyncInitialData', 'data'], props),
          [GC.FIELD_TEL, GC.FIELD_DOCUMENTS],
          props,
        );

        const options = {
          tel,
          isMulti: true,
          templateOptions,
          selectedRate: rate,
          withDispatchDocs: true,
          asyncInitialData: { data: documents },
          customHandleSubmit: handleDispatchCarrierTelRate,
          branchGuid: G.getPropFromObject(GC.BRANCH_GUID, tel),
          handleDispatchCarrierRateWithTerminalData: handleDispatchTelRate,
        };

        return handleDispatchCarrierRate(options);
      }

      G.callFunction(handleDispatchTelRate);
    },
    handleCancelDispatchTelRate: ({ tel: { guid, order }, dispatchTelRateRequest }) => () => (
      dispatchTelRateRequest({
        guid,
        order,
        dispatchAction: GC.CANCEL_DISPATCH_ACTION,
      })
    ),
  }),
  pure,
);

export const withAsyncContacts = compose(
  withState('contactList', 'setContactList', []),
  withHandlers({
    handleGetContacts: (props) => async () => {
      const { branchGuid, setContactList } = props;

      const currentUserBranchGuid = G.getAmousCurrentUserBranchGuidFromWindow();
      const options = {
        params: { [GC.BRANCH_GUID]: R.or(branchGuid, currentUserBranchGuid) },
      };

      const res = await sendRequest('post', endpointsMap.contactBookList, options);

      const { data, status } = res;

      if (G.isResponseSuccess(status)) {
        const contactList = R.map(
          ({ email, lastName, firstName }) => ({
            [GC.FIELD_EMAIL]: email,
            [GC.FIELD_LAST_NAME]: R.or(lastName, []),
            [GC.FIELD_FIRST_NAME]: R.or(firstName, []),
          }),
          R.or(data, []),
        );
        setContactList(contactList);
      } else {
        G.handleFailResponseSimple(res);
      }
    },
  }),
  lifecycle({
    componentDidMount() {
      this.props.handleGetContacts();
    },
  }),
);

const deadheadCallback = (props) => (callbackProps) => {
  const { newValues, setValues } = props;

  const { startPointLocation, finishPointLocation } = newValues;

  const totalDistance = R.path(['data', 'totalDistance'], callbackProps);
  const totalUnit = R.path(['data', 'unit'], callbackProps);
  const stopResults = R.path(['data', 'stopResults'], callbackProps);

  if (G.isAllTrue(
    G.isNotNilAndNotEmpty(totalDistance),
    G.isNotNilAndNotEmpty(startPointLocation),
    G.isNotNilAndNotEmpty(finishPointLocation),
  )) {
    const start = R.head(stopResults);
    const finish = R.last(stopResults);
    const deadhead = G.toFixed(R.add(R.prop('distance', start), R.prop('distance', finish)));

    return setValues({
      ...newValues,
      [GC.FIELD_DEADHEAD_DISTANCE]: deadhead,
      [GC.FIELD_DEADHEAD_DISTANCE_UOM]: totalUnit,
    });
  }

  if (G.isAllTrue(
    G.isNotNilAndNotEmpty(totalDistance),
    G.isNilOrEmpty(startPointLocation),
    G.isNotNilAndNotEmpty(finishPointLocation),
  )) {
    const finish = R.last(stopResults);
    const deadhead = G.toFixed(R.prop('distance', finish));

    return setValues({
      ...newValues,
      [GC.FIELD_DEADHEAD_DISTANCE]: deadhead,
      [GC.FIELD_DEADHEAD_DISTANCE_UOM]: totalUnit,
    });
  }

  if (G.isAllTrue(
    G.isNotNilAndNotEmpty(totalDistance),
    G.isNotNilAndNotEmpty(startPointLocation),
    G.isNilOrEmpty(finishPointLocation),
  )) {
    const start = R.head(stopResults);
    const deadhead = G.toFixed(R.prop('distance', start));

    return setValues({
      ...newValues,
      [GC.FIELD_DEADHEAD_DISTANCE]: deadhead,
      [GC.FIELD_DEADHEAD_DISTANCE_UOM]: totalUnit,
    });
  }
};

const setStartFinishPointLocationsWithDeadhead = (
  props,
  fieldName,
  newValues,
  mappedLocation,
) => {
  const {
    values,
    setValues,
    availableDriversData,
    getAsyncDistanceCalcWithCallback,
  } = props;

  const branchGuid = G.getAmousCurrentBranchGuidFromWindow();

  const { startPointLocation, finishPointLocation } = values;

  const firstEventCoordinates = R.path(['firstEventCoordinates'], availableDriversData);
  const lastEventCoordinates = R.path(['lastEventCoordinates'], availableDriversData);

  if (R.equals(fieldName, GC.FIELD_START_POINT_LOCATION)) {
    let stopPoints = [
      mappedLocation,
      firstEventCoordinates,
      lastEventCoordinates,
    ];

    if (G.isNotNilAndNotEmpty(finishPointLocation)) {
      stopPoints = R.append(finishPointLocation, stopPoints);
    }

    getAsyncDistanceCalcWithCallback({
      stopPoints,
      branchGuid,
      callback: deadheadCallback({ newValues, fieldName, setValues, getAsyncDistanceCalcWithCallback }),
    });
  }

  if (R.equals(fieldName, GC.FIELD_FINISH_POINT_LOCATION)) {
    let stopPoints = [
      firstEventCoordinates,
      lastEventCoordinates,
      mappedLocation,
    ];

    if (G.isNotNilAndNotEmpty(startPointLocation)) {
      stopPoints = R.prepend(startPointLocation, stopPoints);
    }

    getAsyncDistanceCalcWithCallback({
      stopPoints,
      branchGuid,
      callback: deadheadCallback({ newValues, fieldName, setValues, getAsyncDistanceCalcWithCallback }),
    });
  }
};

const setSourceToStartPoint = (values, sourceType, sourceName) => {
  const startPoint = R.path([GC.FIELD_START_POINT_LOCATION], values);

  if (R.not(G.isObject(values))) return values;

  if (G.isNilOrEmpty(startPoint)) return values;

  const newStartPoint = {
    ...startPoint,
    sourceType,
    sourceName,
  };

  return R.assoc(GC.FIELD_START_POINT_LOCATION, newStartPoint, values);
};

export const withSearchLocation = compose(
  connect(
    null,
    {
      openModal, closeModal, openLoader, closeLoader,
    },
  ),
  withAsyncSearchTemplateAutocomplete(),
  withHandlers({
    handleSearchLocation: (props) => (fieldProps) => {
      const { fieldName, fieldType } = fieldProps;
      const { values, openModal, setValues, closeModal } = props;

      const branchGuid = G.getAmousCurrentBranchGuidFromWindow();

      const searchTemplateRequest = (location) => {
        const mappedLocation = G.mapSearchedLocation(location);

        const mappedLocationWithNewAddress1 = R.assoc(
          GC.FIELD_ADDRESS_1,
          G.concatLocationFields(mappedLocation),
          mappedLocation,
        );

        if (R.equals(fieldType, 'locationObject')) {
          let newValues = R.assoc(fieldName, mappedLocationWithNewAddress1, R.or(values, {}));

          if (R.equals(fieldName, GC.FIELD_START_POINT_LOCATION)) {
            newValues = setSourceToStartPoint(
              newValues,
              GC.START_POINT_SOURCE_TYPE_MANUAL,
              G.getAmousCurrentUserLoginIdFromWindow(),
            );
          }

          setValues(newValues);

          setStartFinishPointLocationsWithDeadhead(props, fieldName, newValues, mappedLocation);

          return closeModal();
        }

        setValues(R.mergeRight(values, mappedLocationWithNewAddress1));

        closeModal();
      };

      const modalContent = (
        <LocationTemplateSearch
          branchGuid={branchGuid}
          closeModal={closeModal}
          searchTemplateRequest={searchTemplateRequest}
        />
      );

      const modal = G.createCommonModalOptions(modalContent);

      openModal(modal);
    },
    handleSelectLocation: (props) => (fromInputData) => {
      const { location, fieldName, newValues } = fromInputData;

      let valuesToUse = newValues;

      if (R.equals(fieldName, GC.FIELD_START_POINT_LOCATION)) {
        valuesToUse = setSourceToStartPoint(
          newValues,
          GC.START_POINT_SOURCE_TYPE_MANUAL,
          G.getAmousCurrentUserLoginIdFromWindow(),
        );
      }

      return setStartFinishPointLocationsWithDeadhead(props, fieldName, valuesToUse, location);
    },
    handleChangeSelectLocation: (props) => (fromInputData) => {
      const { value, fieldProps } = fromInputData;

      if (G.isNotNilAndNotEmpty(value)) return;

      const { values, setValues } = props;
      const { id } = fieldProps;

      const defaultUomSystem = G.getConfigGeneralUomCalcDefaultUomSystemFromWindow();
      const deadheadUom = R.pathOr(GC.UOM_MILE, [defaultUomSystem], GC.uomSystemToDistanceUomMap);

      const newValues = {
        ...values,
        [id]: value,
        [GC.FIELD_DEADHEAD_DISTANCE]: 0,
        [GC.FIELD_DEADHEAD_DISTANCE_UOM]: deadheadUom,
      };

      setValues(newValues);
    },
  }),
  pure,
);

export const withAsyncDeadheadMiles = compose(
  withAsyncDistanceCalc,
  pure,
);

export const withShowIntegrationErrorsOnAvailableRates = compose(
  withState('animationDuration', 'setAnimationDuration', '.6s'),
  withHandlers({
    handleShowIntegrationErrors: (props) => () => {
      const { openModal, setAnimationDuration } = props;

      const component = <IntegrationErrors {...props} />;

      const modal = {
        p: 15,
        component,
        options: {
          width: 'auto',
          height: 'auto',
          withCloseIcon: true,
          title: G.getWindowLocale('titles:integration-errors', 'Integration Errors'),
        },
      };

      openModal(modal);
      setAnimationDuration('0s');
    },
  }),
);
